import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  Plus,
  Trash2,
  Save,
  Send,
  Eye,
  X,
  Calendar,
  DollarSign,
  Percent,
  FileText,
  User,
  Scale,
  Calculator
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const InvoiceFormPage = () => {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const { hasPermission, user } = useAdminAuth();
  const isEditMode = !!invoiceId;

  // Form State
  const [formData, setFormData] = useState({
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    status: 'pending',
    currency: 'SAR',
    clientId: '',
    caseId: '',
    items: [
      { id: 1, name: '', description: '', price: 0, quantity: 1, total: 0 }
    ],
    discountType: 'percentage', // percentage or amount
    discountValue: 0,
    taxPercentage: 15,
    notes: '',
    // Installment
    hasInstallment: false,
    installmentCount: 0,
    installments: []
  });

  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);

  // Mock data - سيتم استبداله بـ API
  useEffect(() => {
    // Load clients
    setClients([
      { id: 'CL-001', name: 'محمد أحمد', type: 'فرد', phone: '+966 50 123 4567', email: 'mohamed@example.com' },
      { id: 'CL-002', name: 'شركة النور التجارية', type: 'شركة', phone: '+966 50 234 5678', email: 'info@alnoor.com' },
      { id: 'CL-003', name: 'فاطمة علي', type: 'فرد', phone: '+966 50 345 6789', email: 'fatima@example.com' }
    ]);

    // Load cases
    setCases([
      { id: 'WZ-2024-001', title: 'قضية تجارية', clientId: 'CL-001' },
      { id: 'WZ-2024-002', title: 'قضية عمالية', clientId: 'CL-002' },
      { id: 'WZ-2024-015', title: 'قضية عقارية', clientId: 'CL-003' }
    ]);

    // If edit mode, load invoice data
    if (isEditMode) {
      setLoading(true);
      // Simulate API call
      setTimeout(() => {
        setFormData({
          issueDate: '2024-12-01',
          dueDate: '2024-12-15',
          status: 'pending',
          currency: 'SAR',
          clientId: 'CL-001',
          caseId: 'WZ-2024-001',
          items: [
            { id: 1, name: 'استشارة قانونية', description: 'استشارة شاملة', price: 3000, quantity: 1, total: 3000 },
            { id: 2, name: 'رسوم إدارية', description: 'رسوم معالجة', price: 500, quantity: 1, total: 500 }
          ],
          discountType: 'percentage',
          discountValue: 0,
          taxPercentage: 15,
          notes: '',
          hasInstallment: false,
          installmentCount: 0,
          installments: []
        });
        setSelectedClient(clients[0]);
        setLoading(false);
      }, 500);
    }
  }, [invoiceId, isEditMode]);

  // Calculations
  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.total || 0), 0);
    
    let discount = 0;
    if (formData.discountType === 'percentage') {
      discount = (subtotal * formData.discountValue) / 100;
    } else {
      discount = formData.discountValue;
    }

    const afterDiscount = subtotal - discount;
    const tax = (afterDiscount * formData.taxPercentage) / 100;
    const total = afterDiscount + tax;

    return {
      subtotal,
      discount,
      afterDiscount,
      tax,
      total
    };
  };

  const totals = calculateTotals();

  // Handlers
  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setSelectedClient(client);
    setFormData({ ...formData, clientId, caseId: '' });
  };

  const handleAddItem = () => {
    const newItem = {
      id: Date.now(),
      name: '',
      description: '',
      price: 0,
      quantity: 1,
      total: 0
    };
    setFormData({
      ...formData,
      items: [...formData.items, newItem]
    });
  };

  const handleRemoveItem = (itemId) => {
    if (formData.items.length === 1) {
      alert('يجب أن تحتوي الفاتورة على صنف واحد على الأقل');
      return;
    }
    setFormData({
      ...formData,
      items: formData.items.filter(item => item.id !== itemId)
    });
  };

  const handleItemChange = (itemId, field, value) => {
    const updatedItems = formData.items.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, [field]: value };
        // Calculate total for this item
        updatedItem.total = updatedItem.price * updatedItem.quantity;
        return updatedItem;
      }
      return item;
    });
    setFormData({ ...formData, items: updatedItems });
  };

  const handleInstallmentChange = (hasInstallment) => {
    setFormData({
      ...formData,
      hasInstallment,
      installmentCount: hasInstallment ? 2 : 0,
      installments: hasInstallment ? generateInstallments(2, totals.total) : []
    });
  };

  const generateInstallments = (count, totalAmount) => {
    const installmentAmount = totalAmount / count;
    const installments = [];
    const startDate = new Date(formData.dueDate || new Date());

    for (let i = 0; i < count; i++) {
      const dueDate = new Date(startDate);
      dueDate.setMonth(dueDate.getMonth() + i);
      
      installments.push({
        id: i + 1,
        amount: installmentAmount,
        dueDate: dueDate.toISOString().split('T')[0],
        status: 'pending'
      });
    }

    return installments;
  };

  const handleInstallmentCountChange = (count) => {
    const numCount = parseInt(count);
    setFormData({
      ...formData,
      installmentCount: numCount,
      installments: generateInstallments(numCount, totals.total)
    });
  };

  const handleSave = async (saveType) => {
    // Validation
    if (!formData.clientId) {
      alert('يرجى اختيار العميل');
      return;
    }

    if (!formData.dueDate) {
      alert('يرجى تحديد تاريخ الاستحقاق');
      return;
    }

    if (formData.items.some(item => !item.name || item.price <= 0)) {
      alert('يرجى إكمال بيانات جميع الأصناف');
      return;
    }

    setLoading(true);

    // Simulate API call
    setTimeout(() => {
      if (saveType === 'draft') {
        alert('تم حفظ الفاتورة كمسودة بنجاح');
      } else if (saveType === 'save-and-send') {
        alert('تم حفظ الفاتورة وإرسالها للعميل بنجاح');
      } else {
        alert('تم حفظ الفاتورة بنجاح');
      }
      setLoading(false);
      navigate('/admin/finance/invoices');
    }, 1000);
  };

  const handleCancel = () => {
    if (window.confirm('هل أنت متأكد من الإلغاء؟ سيتم فقدان جميع البيانات غير المحفوظة.')) {
      navigate('/admin/finance/invoices');
    }
  };

  const filteredCases = cases.filter(c => c.clientId === formData.clientId);

  if (loading && isEditMode) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل بيانات الفاتورة...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/finance/invoices')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#1E3A8A] mb-4 transition-colors"
          >
            <ArrowRight className="w-5 h-5" />
            <span className="font-semibold">العودة للفواتير</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2">
                {isEditMode ? 'تعديل فاتورة' : 'إنشاء فاتورة جديدة'}
              </h1>
              <p className="text-gray-600">
                {isEditMode ? `تعديل الفاتورة رقم ${invoiceId}` : 'إنشاء فاتورة جديدة للعميل'}
              </p>
            </div>

            <button
              onClick={() => setShowPreview(true)}
              className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-lg font-bold hover:bg-blue-50 transition-all"
            >
              <Eye className="w-5 h-5" />
              <span>معاينة</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* 1️⃣ Invoice Data */}
            <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
              <h2 className="text-xl font-black text-[#1E3A8A] mb-4 flex items-center gap-2">
                <FileText className="w-6 h-6" />
                <span>بيانات الفاتورة</span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    تاريخ الإصدار *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="date"
                      value={formData.issueDate}
                      onChange={(e) => setFormData({ ...formData, issueDate: e.target.value })}
                      className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    تاريخ الاستحقاق *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                      className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    الحالة
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  >
                    <option value="pending">معلقة</option>
                    <option value="paid">مدفوعة</option>
                    <option value="overdue">متأخرة</option>
                    <option value="cancelled">ملغاة</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    العملة
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  >
                    <option value="SAR">ريال سعودي (SAR)</option>
                    <option value="USD">دولار أمريكي (USD)</option>
                    <option value="EUR">يورو (EUR)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 2️⃣ Client Data */}
            <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
              <h2 className="text-xl font-black text-[#1E3A8A] mb-4 flex items-center gap-2">
                <User className="w-6 h-6" />
                <span>بيانات العميل</span>
              </h2>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  اختر العميل *
                </label>
                <select
                  value={formData.clientId}
                  onChange={(e) => handleClientChange(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  required
                >
                  <option value="">-- اختر العميل --</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name} ({client.type})
                    </option>
                  ))}
                </select>
              </div>

              {selectedClient && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">النوع:</span>
                    <span className="font-semibold text-gray-800">{selectedClient.type}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">الهاتف:</span>
                    <span className="font-semibold text-gray-800">{selectedClient.phone}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">البريد:</span>
                    <span className="font-semibold text-gray-800">{selectedClient.email}</span>
                  </div>
                </div>
              )}
            </div>

            {/* 3️⃣ Optional Case Link */}
            {hasPermission('legal.view') && formData.clientId && filteredCases.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
                <h2 className="text-xl font-black text-[#1E3A8A] mb-4 flex items-center gap-2">
                  <Scale className="w-6 h-6" />
                  <span>ربط بقضية (اختياري)</span>
                </h2>

                <select
                  value={formData.caseId}
                  onChange={(e) => setFormData({ ...formData, caseId: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="">-- بدون قضية --</option>
                  {filteredCases.map(caseItem => (
                    <option key={caseItem.id} value={caseItem.id}>
                      {caseItem.id} - {caseItem.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* 4️⃣ Items Table */}
            <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-black text-[#1E3A8A] flex items-center gap-2">
                  <FileText className="w-6 h-6" />
                  <span>الأصناف والخدمات</span>
                </h2>
                <button
                  onClick={handleAddItem}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-all"
                >
                  <Plus className="w-5 h-5" />
                  <span>إضافة صنف</span>
                </button>
              </div>

              <div className="space-y-4">
                {formData.items.map((item, index) => (
                  <div key={item.id} className="border-2 border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-bold text-gray-700">صنف #{index + 1}</span>
                      {formData.items.length > 1 && (
                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          اسم الخدمة *
                        </label>
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => handleItemChange(item.id, 'name', e.target.value)}
                          placeholder="مثال: استشارة قانونية"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                          required
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          الوصف
                        </label>
                        <textarea
                          value={item.description}
                          onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                          placeholder="وصف تفصيلي للخدمة"
                          rows="2"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none resize-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          السعر (ر.س) *
                        </label>
                        <input
                          type="number"
                          value={item.price}
                          onChange={(e) => handleItemChange(item.id, 'price', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          الكمية *
                        </label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(item.id, 'quantity', parseInt(e.target.value) || 1)}
                          min="1"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                          required
                        />
                      </div>

                      <div className="md:col-span-2">
                        <div className="bg-blue-50 rounded-lg p-3 flex items-center justify-between">
                          <span className="text-sm font-semibold text-blue-800">الإجمالي الفرعي:</span>
                          <span className="text-xl font-black text-[#1E3A8A]">{item.total.toLocaleString()} ر.س</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 5️⃣ Calculations */}
            <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
              <h2 className="text-xl font-black text-[#1E3A8A] mb-4 flex items-center gap-2">
                <Calculator className="w-6 h-6" />
                <span>العمليات الحسابية</span>
              </h2>

              <div className="space-y-4">
                {/* Discount */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      نوع الخصم
                    </label>
                    <select
                      value={formData.discountType}
                      onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                    >
                      <option value="percentage">نسبة مئوية (%)</option>
                      <option value="amount">مبلغ ثابت (ر.س)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      قيمة الخصم
                    </label>
                    <div className="relative">
                      {formData.discountType === 'percentage' ? (
                        <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      ) : (
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      )}
                      <input
                        type="number"
                        value={formData.discountValue}
                        onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) || 0 })}
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-2 pl-10 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Tax */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    نسبة الضريبة (%)
                  </label>
                  <input
                    type="number"
                    value={formData.taxPercentage}
                    onChange={(e) => setFormData({ ...formData, taxPercentage: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="100"
                    step="0.01"
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  />
                </div>

                {/* Installment */}
                {hasPermission('finance.installments') && (
                  <div className="border-t-2 border-gray-200 pt-4">
                    <div className="flex items-center gap-3 mb-4">
                      <input
                        type="checkbox"
                        id="hasInstallment"
                        checked={formData.hasInstallment}
                        onChange={(e) => handleInstallmentChange(e.target.checked)}
                        className="w-5 h-5"
                      />
                      <label htmlFor="hasInstallment" className="text-sm font-bold text-gray-700">
                        تفعيل نظام التقسيط
                      </label>
                    </div>

                    {formData.hasInstallment && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          عدد الأقساط
                        </label>
                        <input
                          type="number"
                          value={formData.installmentCount}
                          onChange={(e) => handleInstallmentCountChange(e.target.value)}
                          min="2"
                          max="12"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                        />

                        {formData.installments.length > 0 && (
                          <div className="mt-4 space-y-2">
                            <p className="text-sm font-semibold text-gray-700">جدول الأقساط:</p>
                            {formData.installments.map((inst, idx) => (
                              <div key={inst.id} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                                <span className="text-sm text-gray-700">القسط #{idx + 1}</span>
                                <span className="text-sm font-semibold text-gray-800">{inst.amount.toLocaleString()} ر.س</span>
                                <span className="text-xs text-gray-500">{inst.dueDate}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    ملاحظات
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="أي ملاحظات إضافية..."
                    rows="3"
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none resize-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Summary */}
          <div className="space-y-6">
            {/* Financial Summary */}
            <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6 sticky top-6">
              <h2 className="text-xl font-black text-[#1E3A8A] mb-4">الملخص المالي</h2>

              <div className="space-y-3">
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-600">الإجمالي الفرعي</span>
                  <span className="font-semibold text-gray-800">{totals.subtotal.toLocaleString()} ر.س</span>
                </div>

                {totals.discount > 0 && (
                  <div className="flex items-center justify-between py-2 text-green-600">
                    <span>الخصم</span>
                    <span className="font-semibold">- {totals.discount.toLocaleString()} ر.س</span>
                  </div>
                )}

                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-600">الضريبة ({formData.taxPercentage}%)</span>
                  <span className="font-semibold text-gray-800">{totals.tax.toLocaleString()} ر.س</span>
                </div>

                <div className="border-t-2 border-gray-200 pt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-gray-800">الإجمالي النهائي</span>
                    <span className="text-2xl font-black text-[#1E3A8A]">{totals.total.toLocaleString()} ر.س</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2 pt-6 border-t-2 border-gray-200 mt-6">
                <button
                  onClick={() => handleSave('save')}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white rounded-lg font-bold hover:shadow-lg transition-all disabled:opacity-50"
                >
                  <Save className="w-5 h-5" />
                  <span>{loading ? 'جاري الحفظ...' : 'حفظ'}</span>
                </button>

                <button
                  onClick={() => handleSave('draft')}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-all disabled:opacity-50"
                >
                  <FileText className="w-5 h-5" />
                  <span>حفظ كمسودة</span>
                </button>

                {hasPermission('finance.send') && (
                  <button
                    onClick={() => handleSave('save-and-send')}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-green-600 text-green-600 rounded-lg font-bold hover:bg-green-50 transition-all disabled:opacity-50"
                  >
                    <Send className="w-5 h-5" />
                    <span>حفظ وإرسال</span>
                  </button>
                )}

                <button
                  onClick={handleCancel}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-red-600 text-red-600 rounded-lg font-bold hover:bg-red-50 transition-all disabled:opacity-50"
                >
                  <X className="w-5 h-5" />
                  <span>إلغاء</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal - سيتم بناؤها في خطوة لاحقة */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-black text-[#1E3A8A]">معاينة الفاتورة</h2>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="bg-gray-100 p-8 rounded-lg">
                <p className="text-center text-gray-600">
                  معاينة الفاتورة قيد التطوير...
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default InvoiceFormPage;
